import React, { useState } from "react";
import {
    Modal,
    Upload,
    Button,
    Steps,
    Card,
    Typography,
    Table,
    Tag,
    Alert,
    Descriptions,
    Space,
    Progress,
    message,
    Tooltip
} from "antd";
import {
    InboxOutlined,
    DownloadOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    FileExcelOutlined,
    RocketOutlined,
    ArrowLeftOutlined,
    DatabaseOutlined
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { PostBbddActivitatIdProblema } from "../../../../dao/bbdd/PostBbddActivitatIdProblema";

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

interface Props {
    visible: boolean;
    id_activitat: string;
    onClose: () => void;
    onSuccess: () => void;
}

export interface ParsedBbddExercici {
    nom: string;
    descripcio: string;
    enunciat: string;
    tipus: string; // ER, Script, Relacional, DDL, SQL, PLSQL, DCL, NoSQL
    dificultat: string; // beginner | intermediate | expert
    tags: string;
    correccio: string;
    checks: string;
    penalitzacio: number;
}

const VALID_TIPUS = [
    'ER',
    'SCRIPT',
    'RELACIONAL',
    'DDL',
    'SQL',
    'SQL:SELECT',
    'SQL:INSERT',
    'SQL:UPDATE',
    'SQL:DELETE',
    'PLSQL',
    'PLSQL:PROCEDURE',
    'PLSQL:FUNCTION',
    'PLSQL:TRIGGER',
    'PLSQL:VIEW',
    'DCL',
    'NOSQL'
];

export const ImportarExercicisBbddExcelModal: React.FC<Props> = ({
    visible,
    id_activitat,
    onClose,
    onSuccess
}) => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [parsedExercicis, setParsedExercicis] = useState<ParsedBbddExercici[]>([]);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitProgress, setSubmitProgress] = useState<number>(0);
    const [statusMessage, setStatusMessage] = useState<string>("");

    const resetState = () => {
        setCurrentStep(0);
        setParsedExercicis([]);
        setValidationErrors([]);
        setValidationWarnings([]);
        setIsSubmitting(false);
        setSubmitProgress(0);
        setStatusMessage("");
    };

    const handleClose = () => {
        if (!isSubmitting) {
            resetState();
            onClose();
        }
    };

    // 1. Descarregar Plantilla Excel Oficial per BBDD
    const handleDownloadTemplate = () => {
        try {
            const wb = XLSX.utils.book_new();

            // Sheet 1: Exercicis
            const headers = [
                "Nom",
                "Tipus",
                "Dificultat",
                "Tags",
                "Enunciat",
                "Solucio_O_Query",
                "Checks_O_Rubrica",
                "PenalitzacioPerIntent"
            ];

            const sampleRows = [
                [
                    "Model Entitat-Relació de Vendes",
                    "ER",
                    "intermediate",
                    "vendes, disseny, er",
                    "### Enunciat\nDissenya el model Entitat-Relació per gestionar clients, comandes i productes en una botiga online.",
                    '{"entities":[{"id":"e1","name":"CLIENT"},{"id":"e2","name":"COMANDA"},{"id":"e3","name":"PRODUCTE"}]}',
                    '{"items":[{"id":"e_CLIENT","label":"Entitat CLIENT","enabled":true,"points":30},{"id":"e_COMANDA","label":"Entitat COMANDA","enabled":true,"points":35},{"id":"e_PRODUCTE","label":"Entitat PRODUCTE","enabled":true,"points":35}]}',
                    5
                ],
                [
                    "Script Inicial de Dades",
                    "Script",
                    "beginner",
                    "script, dml, inicialitzacio",
                    "Carrega de dades relacional per inicialitzar la base de dades amb les categories principals.",
                    "INSERT INTO categories (id, nom) VALUES (1, 'Electrònica'), (2, 'Llibres');",
                    "INSERT",
                    0
                ],
                [
                    "Creació de Taules DDL",
                    "DDL",
                    "intermediate",
                    "ddl, schema, claus",
                    "Crea les taules departament i empleat amb les seves claus primàries i foranes corresponents.",
                    "CREATE TABLE departament (id INT PRIMARY KEY, nom VARCHAR(100));\nCREATE TABLE empleat (id INT PRIMARY KEY, nom VARCHAR(100), dept_id INT, FOREIGN KEY (dept_id) REFERENCES departament(id));",
                    "PRIMARY KEY, FOREIGN KEY",
                    10
                ],
                [
                    "Consulta Empleats amb Salari Alt",
                    "SQL",
                    "beginner",
                    "select, where, orderby",
                    "Retorna el nom i cognom dels empleats amb un salari superior a 3000 ordenats alfabèticament.",
                    "SELECT nom, cognom FROM empleat WHERE salari > 3000 ORDER BY cognom;",
                    "SELECT, WHERE, ORDER BY",
                    5
                ],
                [
                    "Funció Càlcul Descompte",
                    "PLSQL",
                    "expert",
                    "plsql, funcio, determinista",
                    "Crea una funció calcular_descompte(p_id INT) que retorni el percentatge de descompte.",
                    "CREATE FUNCTION calcular_descompte(p_id INT) RETURNS DECIMAL(5,2) DETERMINISTIC BEGIN RETURN 10.0; END;",
                    "FUNCTION, RETURNS",
                    0
                ],
                [
                    "Assignació de Privilegis",
                    "DCL",
                    "intermediate",
                    "dcl, seguretat, grant",
                    "Concedeix permisos de només lectura sobre la taula clients a l'usuari consultor.",
                    "GRANT SELECT ON botiga.clients TO 'consultor'@'localhost';",
                    "GRANT, SELECT",
                    0
                ],
                [
                    "Col·lecció de Logs NoSQL",
                    "NoSQL",
                    "intermediate",
                    "nosql, mongo, insert",
                    "Inserta un document JSON estructurat amb el registre d'auditoria d'accés.",
                    "db.logs.insertOne({ data: new Date(), accio: 'LOGIN', usuari: 'admin' })",
                    "insertOne",
                    5
                ]
            ];

            const wsExercicis = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
            wsExercicis["!cols"] = [
                { wch: 30 },
                { wch: 15 },
                { wch: 15 },
                { wch: 25 },
                { wch: 55 },
                { wch: 50 },
                { wch: 45 },
                { wch: 22 }
            ];
            XLSX.utils.book_append_sheet(wb, wsExercicis, "Exercicis_BBDD");

            // Sheet 2: Guia de camps
            const guideRows = [
                ["Guia de Columnes de la Plantilla de BBDD:"],
                ["Nom", "Nom o títol de l'exercici (Obligatori)"],
                ["Tipus", "ER, Script, Relacional, DDL, SQL, PLSQL, DCL, NoSQL (o subtipus com SQL:SELECT, SQL:INSERT)"],
                ["Dificultat", "beginner | intermediate | expert"],
                ["Tags", "Etiquetes separades per coma o espai (ex: sql, joins, examen)"],
                ["Enunciat", "Enunciat complet en format text o Markdown"],
                ["Solucio_O_Query", "Solució oficial esperada (consulta SQL, script DDL, JSON de diagrama ER/Relacional o instrucció NoSQL)"],
                ["Checks_O_Rubrica", "Paraules clau requerides o JSON de rúbrica ponderada sobre 100 punts (ex: {\"items\": [...]})"],
                ["PenalitzacioPerIntent", "Punts a restar de la nota final (0-100) per cada intent fallit previ (ex: 5 o 10)"],
                [""],
                ["Subtipus SQL/PLSQL suportats:"],
                ["SQL:SELECT, SQL:INSERT, SQL:UPDATE, SQL:DELETE"],
                ["PLSQL:PROCEDURE, PLSQL:FUNCTION, PLSQL:TRIGGER, PLSQL:VIEW"],
                ["ER, Script, Relacional (Exercicis de disseny conceptual i lògic)"]
            ];
            const wsGuide = XLSX.utils.aoa_to_sheet(guideRows);
            wsGuide["!cols"] = [{ wch: 25 }, { wch: 80 }];
            XLSX.utils.book_append_sheet(wb, wsGuide, "Guia_i_Tipus");

            XLSX.writeFile(wb, "Plantilla_Exercicis_BBDD.xlsx");
            message.success("Plantilla oficial descarregada correctament!");
        } catch (error) {
            console.error("Error en generar plantilla:", error);
            message.error("Error en descarregar la plantilla Excel");
        }
    };

    // 2. Parse fitxer Excel carregat
    const handleFileUpload = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const buffer = e.target?.result;
                const wb = XLSX.read(buffer, { type: "array" });

                const errors: string[] = [];
                const warnings: string[] = [];

                const sheetName = wb.SheetNames[0];
                const ws = wb.Sheets[sheetName];
                if (!ws) {
                    setValidationErrors(["El fitxer Excel no conté cap full vàlid."]);
                    setCurrentStep(1);
                    return;
                }

                const rawRows: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
                if (!rawRows || rawRows.length === 0) {
                    setValidationErrors(["El full de càlcul està buit. Afegeix exercicis abans d'importar."]);
                    setCurrentStep(1);
                    return;
                }

                const parsedList: ParsedBbddExercici[] = [];

                rawRows.forEach((row, idx) => {
                    const rowNum = idx + 2; // +1 capçalera, +1 1-index
                    const nom = String(row["Nom"] || row["nom"] || "").trim();
                    const rawTipus = String(row["Tipus"] || row["tipus"] || "SQL").trim();
                    const rawDificultat = String(row["Dificultat"] || row["dificultat"] || "beginner").trim().toLowerCase();
                    const tags = String(row["Tags"] || row["tags"] || row["Etiquetes"] || "").trim();
                    const enunciat = String(row["Enunciat"] || row["enunciat"] || row["Descripcio"] || row["descripcio"] || "").trim();
                    const descripcio = String(row["Descripcio"] || row["descripcio"] || enunciat).trim();
                    const correccio = String(row["Solucio_O_Query"] || row["Correccio_Query"] || row["correccio"] || row["Solucio"] || row["solucio"] || "").trim();
                    const checks = String(row["Checks_O_Rubrica"] || row["Checks"] || row["checks"] || row["Rubrica"] || "").trim();
                    const rawPenalty = row["PenalitzacioPerIntent"] ?? row["Penalitzacio"] ?? row["penalitzacio"] ?? row["penalitzacioPerIntent"] ?? 0;
                    const penalitzacio = Math.max(0, Number(rawPenalty) || 0);

                    if (!nom) {
                        errors.push(`Fila ${rowNum}: El camp 'Nom' és obligatori.`);
                    }

                    const tipusUpper = rawTipus.toUpperCase();
                    if (!VALID_TIPUS.includes(tipusUpper)) {
                        warnings.push(`Fila ${rowNum}: Tipus '${rawTipus}' no reconegut oficialment. S'utilitzarà com a 'SQL'.`);
                    }

                    parsedList.push({
                        nom,
                        tipus: VALID_TIPUS.includes(tipusUpper) ? rawTipus : 'SQL',
                        dificultat: ['beginner', 'intermediate', 'expert'].includes(rawDificultat) ? rawDificultat : 'beginner',
                        tags,
                        enunciat: enunciat || nom,
                        descripcio: descripcio || nom,
                        correccio,
                        checks,
                        penalitzacio
                    });
                });

                setParsedExercicis(parsedList);
                setValidationErrors(errors);
                setValidationWarnings(warnings);
                setCurrentStep(1);
            } catch (err: any) {
                console.error("Error processant Excel:", err);
                setValidationErrors([`No s'ha pogut llegir el fitxer: ${err.message || 'Format no vàlid'}`]);
                setCurrentStep(1);
            }
        };
        reader.readAsArrayBuffer(file);
        return false;
    };

    // 3. Crear els exercicis a la base de dades
    const handleSubmitExercises = async () => {
        setIsSubmitting(true);
        setSubmitProgress(0);

        const total = parsedExercicis.length;
        let successCount = 0;

        for (let i = 0; i < total; i++) {
            const ex = parsedExercicis[i];
            setStatusMessage(`Important exercici ${i + 1} de ${total}: "${ex.nom}"...`);

            try {
                let checks = ex.checks || '';
                const t = ex.tipus;
                const isDesign = t === 'ER' || t === 'Script' || t === 'Relacional';

                if (isDesign) {
                    if (checks.includes('{') && checks.includes('"items"')) {
                        try {
                            const hashIdx = checks.indexOf('#');
                            const prefix = hashIdx !== -1 ? checks.slice(0, hashIdx + 1) : '';
                            const jsonStr = hashIdx !== -1 ? checks.slice(hashIdx + 1) : checks;
                            const parsed = JSON.parse(jsonStr);
                            parsed.penalitzacioPerIntent = ex.penalitzacio || 0;
                            checks = prefix + JSON.stringify(parsed);
                        } catch (e) { }
                    } else if (checks.startsWith('{')) {
                        // El usuario puso directamente un JSON de rúbrica
                        const prefix = t === 'ER' ? 'ER-VALIDATION' : (t === 'Script' ? 'SCRIPT-VALIDATION' : 'RELATIONAL-VALIDATION');
                        try {
                            const parsed = JSON.parse(checks);
                            parsed.penalitzacioPerIntent = ex.penalitzacio || 0;
                            checks = `${prefix}#${JSON.stringify(parsed)}`;
                        } catch (e) {
                            checks = `${prefix}#${checks}`;
                        }
                    } else {
                        const prefix = t === 'ER' ? 'ER-VALIDATION' : (t === 'Script' ? 'SCRIPT-VALIDATION' : 'RELATIONAL-VALIDATION');
                        checks = `${prefix}#{"version":1,"penalitzacioPerIntent":${ex.penalitzacio || 0},"items":[]}`;
                    }
                } else {
                    // SQL, PLSQL, DDL, DCL, NoSQL
                    if (t === 'SQL:INSERT' && !checks.includes('DML-')) checks = checks ? `DML-INSERT#${checks}` : 'DML-INSERT';
                    else if (t === 'SQL:UPDATE' && !checks.includes('DML-')) checks = checks ? `DML-UPDATE#${checks}` : 'DML-UPDATE';
                    else if (t === 'SQL:DELETE' && !checks.includes('DML-')) checks = checks ? `DML-DELETE#${checks}` : 'DML-DELETE';
                    else if (t === 'PLSQL:VIEW' && !checks.includes('VISTA-')) checks = checks ? `VISTA-#${checks}` : 'VISTA-';
                    else if (t === 'DDL' && !checks.includes('DDL-')) checks = checks ? `DDL-#${checks}` : 'DDL-';
                    else if (t === 'NoSQL' && !checks.includes('NOSQL-')) checks = checks ? `NOSQL-#${checks}` : 'NOSQL-';

                    if (ex.penalitzacio > 0 && !checks.includes('PENALTY:')) {
                        checks = checks ? `${checks}#PENALTY:${ex.penalitzacio}` : `PENALTY:${ex.penalitzacio}`;
                    }
                }

                await PostBbddActivitatIdProblema(id_activitat, {
                    id: 0,
                    nom: ex.nom,
                    descripcio: ex.descripcio,
                    enunciat: ex.enunciat || ex.descripcio,
                    dificultat: ex.dificultat,
                    tipus: ex.tipus,
                    correccio: ex.correccio,
                    checks: checks,
                    tags: ex.tags || '',
                    img: (ex as any).img || ''
                });
                successCount++;
            } catch (err) {
                console.error(`Error creant exercici ${ex.nom}:`, err);
            }

            setSubmitProgress(Math.round(((i + 1) / total) * 100));
        }

        setIsSubmitting(false);
        message.success(`S'han importat ${successCount} de ${total} exercicis correctament!`);
        onSuccess();
        handleClose();
    };

    const columns = [
        {
            title: "Nom",
            dataIndex: "nom",
            key: "nom",
            render: (text: string, row: ParsedBbddExercici) => (
                <div>
                    <Text strong>{text}</Text>
                    {row.tags && (
                        <div style={{ marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {row.tags.split(/[\s,]+/).filter(Boolean).map((t, idx) => (
                                <Tag key={idx} style={{ fontSize: '0.7rem', padding: '0 4px', margin: 0 }}>
                                    #{t}
                                </Tag>
                            ))}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: "Tipus",
            dataIndex: "tipus",
            key: "tipus",
            width: 110,
            render: (t: string) => <Tag color="geekblue">{t}</Tag>
        },
        {
            title: "Dificultat",
            dataIndex: "dificultat",
            key: "dificultat",
            width: 100,
            render: (d: string) => {
                const color = d === 'expert' ? 'red' : d === 'intermediate' ? 'orange' : 'green';
                return <Tag color={color}>{d}</Tag>;
            }
        },
        {
            title: "Enunciat",
            dataIndex: "enunciat",
            key: "enunciat",
            ellipsis: true
        },
        {
            title: "Solució / Query",
            dataIndex: "correccio",
            key: "correccio",
            ellipsis: true,
            render: (c: string) => c ? <Text code ellipsis style={{ maxWidth: 140 }}>{c}</Text> : <Text type="secondary">-</Text>
        },
        {
            title: "Penalització",
            dataIndex: "penalitzacio",
            key: "penalitzacio",
            width: 95,
            align: 'center' as const,
            render: (p: number) => p > 0 ? <Tag color="volcano">-{p} pts</Tag> : <Text type="secondary">0</Text>
        }
    ];

    return (
        <Modal
            title={
                <Space>
                    <FileExcelOutlined style={{ color: '#237804', fontSize: 22 }} />
                    <span style={{ fontWeight: 700 }}>Importar Exercicis de Base de Dades per Excel</span>
                </Space>
            }
            open={visible}
            onCancel={handleClose}
            width={850}
            destroyOnClose
            footer={null}
        >
            <Steps
                current={currentStep}
                style={{ marginBottom: 24 }}
                items={[
                    { title: "Carregar Excel" },
                    { title: "Validar Dades" },
                    { title: "Importar" }
                ]}
            />

            {/* STEP 0: Upload / Download template */}
            {currentStep === 0 && (
                <div>
                    <Card
                        style={{
                            background: '#f6ffed',
                            borderColor: '#b7eb8f',
                            borderRadius: 12,
                            marginBottom: 20
                        }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                                <Text strong style={{ fontSize: 15, color: '#135200' }}>
                                    Descarrega la Plantilla Excel Estructurada
                                </Text>
                                <Paragraph style={{ margin: '4px 0 12px 0', color: '#389e0d' }}>
                                    Inclou columnes per especificar nom, enunciat, sentència de correcció, dificultat i el tipus d'exercici:
                                    <strong style={{ marginLeft: 4 }}>ER, Script, Relacional, DDL, SQL, PLSQL, DCL, NoSQL</strong>.
                                </Paragraph>
                            </div>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadTemplate}
                                style={{ background: '#237804', borderColor: '#237804', fontWeight: 600 }}
                            >
                                Descarregar Plantilla Excel Oficial
                            </Button>
                        </Space>
                    </Card>

                    <Dragger
                        name="file"
                        accept=".xlsx, .xls"
                        multiple={false}
                        beforeUpload={handleFileUpload}
                        showUploadList={false}
                        style={{ padding: '24px 0', background: '#fafafa', borderRadius: 12 }}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined style={{ color: '#237804', fontSize: 44 }} />
                        </p>
                        <p className="ant-upload-text" style={{ fontSize: 16, fontWeight: 600 }}>
                            Arrossega el teu fitxer Excel o fes clic per seleccionar-lo
                        </p>
                        <p className="ant-upload-hint" style={{ color: '#888' }}>
                            Accepta formats .xlsx i .xls basats en la plantilla oficial de BBDD.
                        </p>
                    </Dragger>
                </div>
            )}

            {/* STEP 1: Validation & Preview */}
            {currentStep === 1 && (
                <div>
                    {validationErrors.length > 0 && (
                        <Alert
                            message="S'han detectat errors en el fitxer"
                            description={
                                <ul style={{ paddingLeft: 20, margin: 0 }}>
                                    {validationErrors.map((err, i) => (
                                        <li key={i}>{err}</li>
                                    ))}
                                </ul>
                            }
                            type="error"
                            showIcon
                            style={{ marginBottom: 16, borderRadius: 8 }}
                        />
                    )}

                    {validationWarnings.length > 0 && (
                        <Alert
                            message="Avisos (No bloquegen la càrrega)"
                            description={
                                <ul style={{ paddingLeft: 20, margin: 0 }}>
                                    {validationWarnings.map((w, i) => (
                                        <li key={i}>{w}</li>
                                    ))}
                                </ul>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16, borderRadius: 8 }}
                        />
                    )}

                    {validationErrors.length === 0 && (
                        <Alert
                            message={`Excel validat amb èxit: ${parsedExercicis.length} exercicis preparats per importar`}
                            type="success"
                            showIcon
                            style={{ marginBottom: 16, borderRadius: 8 }}
                        />
                    )}

                    <Table
                        columns={columns}
                        dataSource={parsedExercicis}
                        rowKey={(r, i) => String(i)}
                        size="small"
                        pagination={{ pageSize: 5 }}
                        style={{ marginBottom: 20 }}
                    />

                    {isSubmitting && (
                        <div style={{ marginBottom: 20, textAlign: 'center' }}>
                            <Progress percent={submitProgress} status="active" />
                            <Text type="secondary">{statusMessage}</Text>
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => setCurrentStep(0)}
                            disabled={isSubmitting}
                        >
                            Pujar un altre fitxer
                        </Button>
                        <Button
                            type="primary"
                            icon={<RocketOutlined />}
                            disabled={validationErrors.length > 0 || parsedExercicis.length === 0 || isSubmitting}
                            loading={isSubmitting}
                            onClick={handleSubmitExercises}
                            style={{ background: '#237804', borderColor: '#237804', fontWeight: 600 }}
                        >
                            Començar la Importació ({parsedExercicis.length})
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ImportarExercicisBbddExcelModal;
